/**
 * Vendor Manage SKU Configuration
 * Configuration for search form and dialog form fields
 */

import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

/**
 * Search form configuration
 */
export const VENDOR_MANAGE_SKU_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'skuCode',
    label: 'vendor.manage_sku.search.sku_code',
    type: 'searchableDropdown',
    placeholder: 'vendor.manage_sku.search.enter_sku_code',
    options: [],
    showClear: true,
    filter: true,
    width: '20%',
  },
  {
    key: 'vendorSkuCode',
    label: 'vendor.manage_sku.search.vendor_sku_code',
    type: 'text',
    placeholder: 'vendor.manage_sku.search.enter_vendor_sku_code',
    showClear: true,
    filter: true,
    width: '15%',
  },
  {
    key: 'vendorProductName',
    label: 'vendor.manage_sku.search.vendor_product_name',
    type: 'text',
    placeholder: 'vendor.manage_sku.search.enter_vendor_product_name',
    showClear: true,
    filter: true,
    width: '15%',
  },
  {
    key: 'status',
    label: 'vendor.manage_sku.search.status',
    type: 'select',
    options: [
      { label: 'vendor.status.active', value: 'ACTIVE' },
      { label: 'vendor.status.inactive', value: 'INACTIVE' },
    ],
    showClear: true,
  },
];

/**
 * Dialog form field configuration
 */
export interface VendorSkuFormField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'number';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  showClear?: boolean;
  filter?: boolean;
  rows?: number;
  min?: number;
}

export const VENDOR_SKU_FORM_FIELDS: VendorSkuFormField[] = [
  {
    key: 'skuCode',
    label: 'vendor.manage_sku.form.sku_code',
    type: 'select',
    placeholder: 'vendor.manage_sku.form.enter_sku_code',
    required: true,
    showClear: true,
    filter: true,
  },
  {
    key: 'vendorSkuCode',
    label: 'vendor.manage_sku.form.vendor_sku_code',
    type: 'text',
    placeholder: 'vendor.manage_sku.form.enter_vendor_sku_code',
    required: true,
  },
  {
    key: 'vendorProductName',
    label: 'vendor.manage_sku.form.product_name',
    type: 'text',
    placeholder: 'vendor.manage_sku.form.enter_product_name',
    required: true,
  },
  {
    key: 'description',
    label: 'vendor.manage_sku.form.description',
    type: 'textarea',
    placeholder: 'vendor.manage_sku.form.enter_description',
    rows: 3,
  },
  {
    key: 'unitPrice',
    label: 'vendor.manage_sku.form.unit_price',
    type: 'number',
    placeholder: 'vendor.manage_sku.form.enter_unit_price',
    required: true,
    min: 1,
  },
  {
    key: 'primaryFlag',
    label: 'vendor.manage_sku.form.primary_flag',
    type: 'checkbox',
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 */
export const VENDOR_MANAGE_SKU_DEFAULT_COLUMNS = {
  SKU_CODE: 'skuCode',
  SKU_NAME: 'skuName',
  VENDOR_SKU_CODE: 'vendorSkuCode',
  VENDOR_PRODUCT_NAME: 'vendorProductName',
  UNIT_PRICE: 'unitPrice',
  DESCRIPTION: 'description',
  PRIMARY_FLAG: 'primaryFlag',
  STATUS: 'active',
} as const;

/**
 * Action column property name
 */
export const VENDOR_MANAGE_SKU_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 */
export const VENDOR_MANAGE_SKU_DEFAULT_COLUMNS_ORDER: string[] = [
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.SKU_CODE,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.SKU_NAME,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.VENDOR_SKU_CODE,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.VENDOR_PRODUCT_NAME,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.UNIT_PRICE,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.DESCRIPTION,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.PRIMARY_FLAG,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.STATUS,
];

/**
 * Table columns configuration
 */
export const VENDOR_MANAGE_SKU_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'vendor.table.columns.sku_code',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.SKU_CODE,
    width: 120,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.sku_name',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.SKU_NAME,
    width: 180,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.vendor_sku_code',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.VENDOR_SKU_CODE,
    width: 150,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.vendor_product_name',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.VENDOR_PRODUCT_NAME,
    width: 180,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.unit_price',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.UNIT_PRICE,
    width: 130,
    align: 'right',
    type: 'currency',
  },
  {
    label: 'vendor.table.columns.description',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.DESCRIPTION,
    width: 200,
    align: 'left',
  },
  {
    label: 'vendor.table.columns.primary_flag',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.PRIMARY_FLAG,
    width: 140,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'vendor.table.columns.status',
    prop: VENDOR_MANAGE_SKU_DEFAULT_COLUMNS.STATUS,
    width: 120,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'common.table.columns.actions',
    prop: VENDOR_MANAGE_SKU_ACTION_COLUMN.ACTIONS,
    width: 200,
    align: 'center',
    type: 'actions',
  },
];
